import type { ColumnDef, ColumnValue } from "./fields";
import { columnDef } from "./fields";
import { tagFieldRef } from "./predicate";

/**
 * Derives a tag's immutable slug from its label: lowercased, whitespace runs replaced by dashes.
 *
 * Must stay in lockstep with the SQL backfill in the `add_workspace_tag_slug` migration
 * (`lower(regexp_replace(label, '\s+', '-', 'g'))`) so slugs minted in code and in SQL agree. (The
 * backfill additionally suffixes the row id onto case-variant label collisions, which cannot occur at
 * this write path: the slug's own uniqueness is enforced on insert.)
 */
export function slugifyTagLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, "-");
}

/**
 * The slug of the tag every ELD attribution strategy writes the integration's name into.
 *
 * Their `defaultLabel` is "Integration". Which integration a driver came from is a backend concern, so no Dispatch
 * surface offers this tag as a column, a sort, a grouping, or a filter. A workspace that relabelled the strategy's tag
 * escapes this check; the strategies never do that on their own.
 */
export const INTEGRATION_TAG_SLUG = slugifyTagLabel("Integration");

/**
 * Whether a tag slug is the integration attribution tag, the one the Dispatch page never shows.
 */
export function isIntegrationTag(slug: string): boolean {
  return slug === INTEGRATION_TAG_SLUG;
}

/**
 * One tag config's computed values: the tag's immutable slug, a display label, and driver→values.
 */
export type CollectedTag = {
  /** The workspace tag's immutable slug — the natural key predicates reference the column by. */
  slug: string;
  label: string;
  /** ELD external id → the driver's calculated tag values (may be empty for a reported-but-filtered driver). */
  values: Map<string, string[]>;
};

/**
 * The driver-table projection of an integration's live tag system.
 */
export type MergedTagColumns = {
  /** One column per distinct tag slug, in first-seen order. */
  columns: ColumnDef[];
  /** Per-driver cell values keyed by ELD external id, one {@link ColumnValue} per column the driver has. */
  valuesByExternalId: Map<string, ColumnValue[]>;
};

/**
 * Folds per-config collected tag values into driver columns and per-driver cells.
 *
 * Configs sharing a tag slug collapse into one column with their values merged and de-duplicated, so the
 * table never sees duplicate column ids. Pure: no I/O, so it stays unit-testable.
 *
 * @param collected - One entry per tag config, each carrying its tag slug/label and driver→values map.
 */
export function mergeTagColumns(collected: CollectedTag[]): MergedTagColumns {
  const columnsByKey = new Map<string, ColumnDef>();
  // externalId -> (column key -> accumulated tag values)
  const cellsByExternalId = new Map<string, Map<string, string[]>>();

  for (const { slug, label, values } of collected) {
    const column = columnDef(tagFieldRef(slug), label);
    if (!columnsByKey.has(column.key)) {
      columnsByKey.set(column.key, column);
    }
    for (const [externalId, tagValues] of values) {
      const cells = cellsByExternalId.get(externalId) ?? new Map<string, string[]>();
      cells.set(column.key, [...(cells.get(column.key) ?? []), ...tagValues]);
      cellsByExternalId.set(externalId, cells);
    }
  }

  const valuesByExternalId = new Map<string, ColumnValue[]>();
  for (const [externalId, cells] of cellsByExternalId) {
    valuesByExternalId.set(
      externalId,
      [...cells.entries()].map(([key, vals]) => {
        const deduped = [...new Set(vals)];
        // Individual values ride alongside the joined display string so predicates and option lists
        // work per value — tag values legitimately contain ", " and must never be re-split from `value`.
        return { key, value: deduped.length > 0 ? deduped.join(", ") : null, values: deduped };
      }),
    );
  }

  return { columns: [...columnsByKey.values()], valuesByExternalId };
}
